import {
  useState,
  useEffect,
  type ChangeEvent,
  type FocusEvent,
  useCallback
} from 'react';
import {
  type Validations,
  type UseInputValidationResult,
  type InputValues,
  type InputErrors,
  type InputTouched,
  type InputValidateFunction
} from './useInputsValidation.types';
import {
  type ValueUpdationConfig,
  type FormValues
} from '../use-form-validation';
import { EMPTY_OBJECT } from '@brahma/utils';

const useInputsValidation = (
  initialValues: InputValues,
  initialValidations: Validations,
  addErrorsWithoutTouched: boolean = false,
  formValues: FormValues = EMPTY_OBJECT
): UseInputValidationResult => {
  const [values, setValues] = useState(initialValues);
  const [validations, setValidations] = useState(initialValidations);
  const [errors, setErrors] = useState<InputErrors>({});
  const [isTouched, setIsTouched] = useState<InputTouched>({});
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);

  useEffect(() => {
    const errorPresent = Object.keys(values).some((errorKey) => {
      const currError = errors[errorKey] ?? '';

      return validations[errorKey]
        ? !isTouched[errorKey] || currError !== ''
        : false;
    });

    setDisableSubmit(errorPresent);
  }, [values, errors]);

  useEffect(() => {
    if (addErrorsWithoutTouched) {
      Object.keys(values).forEach((key) => {
        validate({ name: key, value: values[key]?.toString() });
      });
    }
  }, [values]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setValues((prevValues) => ({ ...prevValues, [name]: value }));

    setIsTouched((prevIsTouched) => ({ ...prevIsTouched, [name]: true }));
    validate({ name, value });
  };

  const updateValues = (key: string, value: string) => {
    setValues((prevValues) => ({ ...prevValues, [key]: value }));

    setIsTouched((prevIsTouched) => ({ ...prevIsTouched, [key]: true }));
    validate({ name: key, value });
  };

  const removeValueByKey = (key: string) => {
    setValues((prevValues) => {
      const { [key]: _, ...rest } = prevValues;
      return rest;
    });
    setErrors((prevErrors) => {
      const { [key]: _, ...rest } = prevErrors;
      return rest;
    });
    setValidations((prevValidations) => {
      const { [key]: _, ...rest } = prevValidations;
      return rest;
    });
  };

  const updateValidation = (
    key: string,
    validationFuncs: InputValidateFunction[]
  ) => {
    setValidations((prevValidations) => ({
      ...prevValidations,
      [key]: validationFuncs
    }));
    validate({ name: key, value: values[key] as string, validationFuncs });
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!isTouched[name]) {
      setIsTouched((prevIsTouched) => ({ ...prevIsTouched, [name]: true }));
    }
    validate({ name, value });
  };

  const reset = useCallback(
    (newValues: InputValues, newValidations: Validations) => {
      setValues(newValues);
      setValidations(newValidations);
      setErrors({});

      setIsTouched({});
      Object.keys(newValues).forEach((key) => {
        if (newValues[key]) {
          validate({ name: key, value: newValues[key] as string });

          setIsTouched((prevIsTouched) => ({ ...prevIsTouched, [key]: true }));
        }
      });
    },
    []
  );

  const validate = ({
    name,
    value,
    validationFuncs: overrideValidationFuncs
  }: {
    name: string;
    value: string;
    validationFuncs?: InputValidateFunction[];
  }) => {
    const validationFuncs = overrideValidationFuncs ?? validations[name];
    const newErrors: InputErrors = {
      [name]: ''
    };
    if (!validationFuncs || validationFuncs.length === 0) {
      setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
      return;
    }

    validationFuncs.some((validationFunc) => {
      const message = validationFunc(value ?? '', { ...formValues, ...values });
      if (message !== '') {
        newErrors[name] = message;
        return true;
      }
      newErrors[name] = '';
      return false;
    });
    setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
  };

  const resetValidation = () => {
    setErrors({});

    setIsTouched({});
  };

  const resetAllValues = useCallback((values: InputValues) => {
    setValues(values);
    setErrors({});

    setIsTouched({});
  }, []);

  const handleSubmit = (onSubmit: () => void) => {
    const errors: InputErrors = {};
    let localDisableSubmit = false;
    Object.keys(values).forEach((name) => {
      const validationFuncs = validations[name];
      if (validationFuncs) {
        const size = validationFuncs.length;
        for (let i = 0; i < (size || 0); i++) {
          const message = validationFuncs[i](values[name] as string, values);
          if (message !== '') {
            errors[name] = message;
            localDisableSubmit = true;
            break;
          } else errors[name] = '';
        }
      }
    });
    setErrors(errors);
    if (localDisableSubmit) {
      setDisableSubmit(true);
    } else {
      onSubmit();
    }
  };

  const updateMultipleValues = (
    newValues: InputValues,
    optionalConfig?: ValueUpdationConfig
  ) => {
    setValues((prevValues) => ({ ...prevValues, ...newValues }));

    optionalConfig?.runValidation &&
      Object.keys(newValues).forEach((key) => {
        validate({ name: key, value: newValues[key] as string });
      });
  };

  const runValidations = () => {
    Object.keys(values).forEach((key) => {
      validate({ name: key, value: values[key]?.toString() });
    });
  };

  const updateIsTouched = (newIsTouched: InputTouched) => {
    setIsTouched((prevIsTouched) => ({ ...prevIsTouched, ...newIsTouched }));
  };

  return {
    values,
    resetAllValues,
    reset,
    errors,
    isTouched,
    handleChange,
    handleBlur,
    disableSubmit,
    resetValidation,
    handleSubmit,
    updateValues,
    updateMultipleValues,
    runValidations,
    updateIsTouched,
    updateValidation,
    removeValueByKey
  };
};

export default useInputsValidation;
